import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PageLayout } from '@/shared/ui/layout'
import { TaskCard } from './components/TaskCard'
import { useAuthStore } from '@/app/store/authStore'
import { notification, Pagination, PaginationProps, Typography } from 'antd'
import { Icon } from '@/shared/ui/Icon/Icon'
import { PagerButton, PaginationContainer } from './styled'

import { TaskTab, TASK_TABS, PAGINATION_OPTIONS } from './lib/taskTypes'
import { TaskItem, TASK_STATUSES } from '@/entities/task/model/taskTypes'
import { AllTasks, TaskStatusType } from './components/AllTasks'
import { useDownloadCredentials } from '@/entities/credentials'
import { transformApiTaskToTaskItem } from '@/entities/task/lib/transformTask'
import {
  fetchTaskById,
  deleteTask,
  suspendTask,
  resumeTask,
} from '@/entities/task/api/tasksApi'
import { tasksKeys } from '@/entities/task/model/tasksKeys'
import {
  syncTaskInCache,
  removeTaskFromCache,
} from '@/entities/task/model/tasksCache'
import { useMyTasksQuery, useAllTasksQuery } from '../model/useTasksQuery'

const itemRender: PaginationProps['itemRender'] = (
  _,
  type,
  originalElement
) => {
  if (type === 'prev') {
    return (
      <PagerButton gap={4} align="center">
        <Icon name="pager_left" size={16} />
      </PagerButton>
    )
  }
  if (type === 'next') {
    return (
      <PagerButton gap={4} align="center">
        <Icon name="pager_right" size={16} />
      </PagerButton>
    )
  }
  return originalElement
}

export const TasksPage: React.FC = () => {
  const [openedLogsTaskId, setOpenedLogsTaskId] = useState<string | null>(null)
  const { user, tokenAccount } = useAuthStore()
  const queryClient = useQueryClient()
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [deletingTasks, setDeletingTasks] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<TaskTab>(TASK_TABS.MY)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [myTasksPageSize, setMyTasksPageSize] = useState<number>(
    PAGINATION_OPTIONS.MY_TASKS_DEFAULT_SIZE
  )
  const [allTasksPageSize, setAllTasksPageSize] = useState<number>(
    PAGINATION_OPTIONS.ALL_TASKS_DEFAULT_SIZE
  )
  const [refreshingTasks, setRefreshingTasks] = useState<Set<string>>(new Set())
  const [activeFilter, setActiveFilter] = useState<TaskStatusType>(
    TASK_TABS.ALL
  )

  const myTasksQuery = useMyTasksQuery(user?.username)
  const allTasksQuery = useAllTasksQuery(activeTab === TASK_TABS.ALL)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const ITEMS_PER_PAGE = useMemo(() => {
    return activeTab === TASK_TABS.MY ? myTasksPageSize : allTasksPageSize
  }, [activeTab, myTasksPageSize, allTasksPageSize])

  useEffect(() => {
    if (user?.id && user.id !== currentUserId) {
      setCurrentUserId(user.id)
      setActiveTab(TASK_TABS.MY)
      setCurrentPage(1)
      setSearchQuery('')
    }
  }, [user?.id, currentUserId])

  const tasks = useMemo(() => {
    const rawTasks = myTasksQuery.data ?? []

    const filteredTasks = rawTasks.filter((task) =>
      activeTab === TASK_TABS.MY ? task.status !== TASK_STATUSES.DELETED : true
    )

    return [...filteredTasks].sort((a, b) => {
      const parseDate = (dateStr: string) => {
        if (!dateStr || dateStr === 'Не указано') return new Date(0)
        try {
          const [datePart, timePart] = dateStr.split(', ')
          const [day, month, year] = datePart.split('.')
          return new Date(`${year}-${month}-${day}T${timePart}`)
        } catch {
          return new Date(0)
        }
      }

      const dateA = parseDate(a.createdAt)
      const dateB = parseDate(b.createdAt)

      return dateB.getTime() - dateA.getTime()
    })
  }, [myTasksQuery.data, activeTab])

  const allTasks = allTasksQuery.data ?? []
  const allTasksFull = allTasksQuery.data ?? []

  const filterTasksBySearch = useCallback(
    (taskList: TaskItem[], query: string) => {
      if (!query.trim()) return taskList

      const searchLower = query.toLowerCase()
      return taskList.filter((task) => {
        const numberMatch = task.number.toString().includes(searchLower)
        const uploadAccountMatch = task.uploadAccount
          .toLowerCase()
          .includes(searchLower)
        const downloadAccountMatch = task.downloadAccount
          .toLowerCase()
          .includes(searchLower)
        const statusMatch = task.status.toLowerCase().includes(searchLower)
        const idMatch = task.id.toLowerCase().includes(searchLower)

        return (
          numberMatch ||
          uploadAccountMatch ||
          downloadAccountMatch ||
          statusMatch ||
          idMatch
        )
      })
    },
    []
  )

  const filteredTaskList = useMemo(() => {
    const currentTaskList = activeTab === TASK_TABS.MY ? tasks : allTasks
    return filterTasksBySearch(currentTaskList, searchQuery)
  }, [activeTab, tasks, allTasks, searchQuery, filterTasksBySearch])

  const loadTaskById = async (taskId: string) => {
    try {
      const apiTask = await fetchTaskById(taskId)

      const transformedTask = transformApiTaskToTaskItem(apiTask)

      if (transformedTask) {
        if (activeTab === TASK_TABS.MY) {
          if (user?.username) {
            syncTaskInCache(
              queryClient,
              tasksKeys.my(user.username),
              transformedTask
            )
          }
        } else {
          syncTaskInCache(queryClient, tasksKeys.allTasks(), transformedTask)
        }
      }
    } catch (error) {
      console.error('❌ Ошибка при загрузке данных задачи:', error)
    }
  }

  const filteredByStatusAndSearch = useMemo(() => {
    let filtered = filterTasksBySearch(
      activeTab === TASK_TABS.MY ? tasks : allTasks,
      searchQuery
    )

    if (activeTab === TASK_TABS.ALL && activeFilter !== TASK_TABS.ALL) {
      filtered = filtered.filter((task) => {
        const status = task.status.toLowerCase()
        switch (activeFilter) {
          case 'processing':
            return (
              status === 'processing' ||
              status === 'in progress' ||
              status === 'running' ||
              status === 'created' ||
              status === 'started' ||
              status === 'pending'
            )
          case 'completed':
            return (
              status === 'successed' ||
              status === 'completed' ||
              status === 'finished'
            )
          case 'failed':
            return status === 'failed' || status === 'error'
          case 'suspended':
            return status === 'suspended'
          case 'deleted':
            return status === 'deleted'
          default:
            return true
        }
      })
    }

    return filtered
  }, [
    activeTab,
    tasks,
    allTasks,
    searchQuery,
    activeFilter,
    filterTasksBySearch,
  ])

  const handleShowLogs = useCallback(
    (taskId: string) => {
      setOpenedLogsTaskId(openedLogsTaskId === taskId ? null : taskId)
    },
    [openedLogsTaskId]
  )

  const handleTabChange = useCallback(
    (key: string) => {
      const newTab = key as TaskTab

      if (newTab === activeTab) return

      setOpenedLogsTaskId(null)

      setActiveTab(newTab)
      setCurrentPage(1)
      setSearchQuery('')
    },
    [activeTab]
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const handleRefresh = useCallback(() => {
    setOpenedLogsTaskId(null)

    if (activeTab === TASK_TABS.MY) {
      myTasksQuery.refetch()
    } else {
      allTasksQuery.refetch()
    }
    setSearchQuery('')
    setCurrentPage(1)
  }, [activeTab, myTasksQuery, allTasksQuery])

  const handleDeleteTask = async (taskId: string) => {
    setDeletingTasks((prev) => new Set([...prev, taskId]))

    try {
      await deleteTask(taskId)

      if (activeTab === TASK_TABS.MY) {
        // "Мои задачи" - удалённая задача пропадает из списка целиком
        if (user?.username) {
          removeTaskFromCache(queryClient, tasksKeys.my(user.username), taskId)
        }
      } else {
        // "Все задачи" - задача остаётся в списке под статусом Deleted
        // (доступна через фильтр "Удалённые")
        const updatedTask = allTasks.find((task) => task.id === taskId)
        if (updatedTask) {
          const deletedTask: TaskItem = {
            ...updatedTask,
            status: TASK_STATUSES.DELETED,
            progress: 0,
          }
          syncTaskInCache(queryClient, tasksKeys.allTasks(), deletedTask)
        }
      }
    } catch (error) {
      console.error('❌ Ошибка при удалении задачи на сервере:', error)
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось удалить задачу на сервере',
      })
    } finally {
      setDeletingTasks((prev) => {
        const newSet = new Set([...prev])
        newSet.delete(taskId)
        return newSet
      })
    }
  }

  const { downloadCredentials } = useDownloadCredentials()

  const handleDownloadCredentials = async (taskId: string) => {
    const currentTaskList = activeTab === TASK_TABS.MY ? tasks : allTasks
    const task = currentTaskList.find((t) => t.id === taskId)
    if (!task) {
      console.error('❌ Задача не найдена в локальном состоянии')
      return
    }

    await downloadCredentials(taskId)
  }

  const handleSuspendTask = async (taskId: string) => {
    setRefreshingTasks((prev) => new Set([...prev, taskId]))
    try {
      const response = await suspendTask(taskId)

      if (response.status === 200 || response.status === 204) {
        const currentTaskList = activeTab === TASK_TABS.MY ? tasks : allTasks
        const updatedTask = currentTaskList.find((task) => task.id === taskId)
        if (updatedTask) {
          const suspendedTask: TaskItem = {
            ...updatedTask,
            status: TASK_STATUSES.SUSPENDED,
          }

          if (activeTab === TASK_TABS.MY) {
            if (user?.username) {
              syncTaskInCache(
                queryClient,
                tasksKeys.my(user.username),
                suspendedTask
              )
            }
          } else {
            syncTaskInCache(queryClient, tasksKeys.allTasks(), suspendedTask)
          }
        }

        await loadTaskById(taskId)
      }
    } catch (error) {
      console.error('❌ Ошибка при приостановке задачи:', error)
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось приостановить задачу',
      })
    } finally {
      setRefreshingTasks((prev) => {
        const newSet = new Set([...prev])
        newSet.delete(taskId)
        return newSet
      })
    }
  }

  const handleResumeTask = async (taskId: string) => {
    try {
      const response = await resumeTask(taskId)

      if (response.status === 200 || response.status === 204) {
        const currentTaskList = activeTab === TASK_TABS.MY ? tasks : allTasks
        const updatedTask = currentTaskList.find((task) => task.id === taskId)
        if (updatedTask) {
          const resumedTask: TaskItem = {
            ...updatedTask,
            status: TASK_STATUSES.STARTED,
          }

          if (activeTab === TASK_TABS.MY) {
            if (user?.username) {
              syncTaskInCache(
                queryClient,
                tasksKeys.my(user.username),
                resumedTask
              )
            }
          } else {
            syncTaskInCache(queryClient, tasksKeys.allTasks(), resumedTask)
          }
        }
        loadTaskById(taskId)
      }
    } catch (error) {
      console.error('❌ Ошибка при возобновлении задачи:', error)
      notification.error({
        message: 'Ошибка',
        description: 'Не удалось возобновить задачу',
      })
    }
  }

  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE

    let filteredList = filteredTaskList
    if (activeTab === TASK_TABS.MY) {
      filteredList = filteredTaskList.filter(
        (task) => task.status !== TASK_STATUSES.DELETED
      )
    }

    return filteredList.slice(startIndex, endIndex)
  }, [filteredTaskList, currentPage, ITEMS_PER_PAGE, activeTab])

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page)
    if (newPageSize) {
      if (activeTab === TASK_TABS.MY) {
        setMyTasksPageSize(newPageSize)
      } else {
        setAllTasksPageSize(newPageSize)
      }
    }
  }

  const currentIsLoading =
    activeTab === TASK_TABS.MY
      ? myTasksQuery.isLoading
      : allTasksQuery.isLoading

  return (
    <PageLayout
      title="Мои задачи"
      subtitle="UTC-3"
      onRefresh={handleRefresh}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      tabs={[
        {
          key: TASK_TABS.MY,
          label: 'Мои задачи',
          icon: 'my_tasks',
        },
        {
          key: TASK_TABS.ALL,
          label: 'Все задачи',
          icon: 'all_tasks',
        },
      ]}
      showSearch={true}
      searchValue={searchQuery}
      onSearchChange={handleSearchChange}
    >
      {currentIsLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Icon name="spinner" size={24} spin />
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: '8px' }}
          >
            {activeTab === TASK_TABS.MY
              ? 'Загрузка ваших задач...'
              : 'Загрузка всех задач...'}
          </Typography.Text>
        </div>
      ) : filteredTaskList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Задачи не найдены</p>
          <p style={{ color: '#666', marginTop: '8px' }}>
            {searchQuery.trim()
              ? 'По вашему запросу ничего не найдено'
              : activeTab === TASK_TABS.MY
                ? 'Создайте новую задачу на странице переноса данных'
                : 'Все задачи отсутствуют или не удалось загрузить'}
          </p>
        </div>
      ) : (
        <>
          {activeTab === TASK_TABS.MY ? (
            <>
              {paginatedTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  showLogs={openedLogsTaskId === task.id}
                  onToggleLogs={() => handleShowLogs(task.id)}
                  onDelete={handleDeleteTask}
                  onDownloadCredentials={handleDownloadCredentials}
                  isDeleting={deletingTasks.has(task.id)}
                />
              ))}
            </>
          ) : (
            <AllTasks
              tasks={filteredByStatusAndSearch}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              allTasksFull={allTasksFull}
              isLoading={allTasksQuery.isLoading}
              onDeleteTask={handleDeleteTask}
              onDownloadCredentials={handleDownloadCredentials}
              onSuspendTask={handleSuspendTask}
              onResumeTask={handleResumeTask}
              deletingTasks={deletingTasks}
              refreshingTasks={refreshingTasks}
              onToggleLogs={handleShowLogs}
              currentUsername={tokenAccount}
            />
          )}
          {activeTab === TASK_TABS.MY &&
          filteredTaskList.length > ITEMS_PER_PAGE ? (
            <PaginationContainer justify="end">
              <Pagination
                current={currentPage}
                total={filteredTaskList.length}
                pageSize={ITEMS_PER_PAGE}
                onChange={handlePageChange}
                showSizeChanger
                pageSizeOptions={[...PAGINATION_OPTIONS.PAGE_SIZE_OPTIONS]}
                showTotal={(total, range) => (
                  <>
                    <span>Записей:</span> {range[0]}-{range[1]} из{' '}
                    <b>{total}</b>
                  </>
                )}
                itemRender={itemRender}
                locale={{
                  items_per_page: '',
                }}
              />
            </PaginationContainer>
          ) : null}
        </>
      )}
    </PageLayout>
  )
}
